import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Alert, Grid, Stack, useTheme, Fade } from '@mui/material';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';
import { ActiveTeamContext } from '../../context/ActiveTeamContext';

import MemberSearchCard from '../../components/teams/MemberSearchCard';
import InviteLinkCard from '../../components/teams/InviteLinkCard';
import MemberRoster from '../../components/teams/MemberRoster';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';

const TeamManagement = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    // Context & Profile
    const { activeTeamId, setActiveTeamId } = useContext(ActiveTeamContext);
    const { data: profile } = useDataFetch({ url: '/users/profile' });

    // Evaluate Role
    const userGlobalRole = profile?.user?.role;
    const activeTeamRole = profile?.teams?.find(t => t?.team_id?.toString() === activeTeamId?.toString())?.rolle;
    const isTrainer = userGlobalRole === 'trainer' || profile?.user?.isAdmin || userGlobalRole === 'super_admin' || activeTeamRole === 'trainer' || activeTeamRole === 'admin';

    // Auto-select first team if no active team is set
    useEffect(() => {
        if (!activeTeamId && profile?.teams?.length > 0) {
            setActiveTeamId(profile.teams[0].team_id?.toString());
        }
    }, [profile, activeTeamId, setActiveTeamId]);

    // Roster Fetch
    const { data: membersData, refetch: refetchMembers } = useDataFetch({ url: activeTeamId ? `/teams/${activeTeamId}/members` : null });

    // Einladungslink generieren
    const { data: iData, error: iError, isLoading: iLoading, postData: generateInvite } = usePostDataFetch();

    // Spieler suchen & Einladen
    const [searchQuery, setSearchQuery] = useState('');
    const { data: searchData, refetch: refetchSearch } = useDataFetch({ url: searchQuery.length > 1 ? `/search?q=${searchQuery}` : null });
    const { data: inviteData, error: inviteErr, isLoading: inviteLoading, postData: inviteMember } = usePostDataFetch();

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.length > 1) refetchSearch();
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleGenerateInvite = (e) => {
        e.preventDefault();
        if (!activeTeamId) return;
        // Temporarily disable failing invite generation until the backend invite flow is stabilized.
        console.warn('Invite generation temporarily disabled for active team:', activeTeamId);
        // generateInvite({ url: `/teams/team/${activeTeamId}/invite` });
    };

    const handleRemoveMember = async (userId, memberName) => {
        if (!window.confirm(`Mitglied ${memberName} wirklich aus dem Team entfernen?`)) return;
        try {
            const token = localStorage.getItem("token");
            const apiUrl = process.env.REACT_APP_API_URL || '';
            await fetch(`${apiUrl}/teams/${activeTeamId}/member/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            refetchMembers();
        } catch (err) {
            console.error(err);
        }
    };

    const handleInviteMember = async (userId) => {
        await inviteMember({ url: `/teams/${activeTeamId}/member`, aBody: { userId } });
        refetchMembers();
    };

    const handleUpdateSecondaryRole = async (userId, sekundaer_rolle) => {
        try {
            const token = localStorage.getItem("token");
            const apiUrl = process.env.REACT_APP_API_URL || '';
            await fetch(`${apiUrl}/teams/${activeTeamId}/member/${userId}/secondary-role`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ sekundaer_rolle })
            });
            refetchMembers();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Fade in={true} timeout={800}>
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, margin: '0 auto', mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 6, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                            Team <span style={{ color: theme.palette.primary.main }}>Management</span>
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 600, opacity: 0.8 }}>
                            Mitglieder verwalten, Rollen zuweisen & Einladungen steuern
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<GroupAddRoundedIcon />}
                            onClick={() => navigate('/teams/invitations')}
                            sx={{
                                borderRadius: "12px",
                                fontWeight: 900,
                                px: 3,
                                py: 1,
                                border: '2px solid',
                                '&:hover': { border: '2px solid', bgcolor: 'rgba(255,255,255,0.05)' }
                            }}
                        >
                            Einladungen
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SettingsRoundedIcon />}
                            // onClick={() => navigate('/settings')}
                            onClick={() => alert("Gibts noch nicht")}
                            sx={{
                                borderRadius: "12px",
                                fontWeight: 900,
                                px: 3,
                                py: 1,
                                bgcolor: 'primary.main',
                                color: '#000',
                                '&:hover': { bgcolor: 'primary.dark' }
                            }}
                        >
                            Einstellungen
                        </Button>
                    </Stack>
                    {isTrainer && (
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => navigate('/teams/create')}
                            startIcon={<AddCircleRoundedIcon />}
                            sx={{
                                borderRadius: "12px",
                                py: 2,
                                fontWeight: 900,
                                border: '1px dashed',
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': { border: '1px solid', bgcolor: 'rgba(190, 242, 100, 0.05)' }
                            }}
                        >
                            Neues Team gründen
                        </Button>
                    )}
                </Box>

                {activeTeamId && profile?.teams?.length !== 0 && (

                    <Grid container spacing={4}>
                        <Grid lg={4}>
                            <Stack spacing={4}>
                                {isTrainer && (
                                    <MemberSearchCard
                                        activeTeamId={activeTeamId}
                                        searchQuery={searchQuery}
                                        setSearchQuery={setSearchQuery}
                                        searchData={searchData}
                                        membersData={membersData}
                                        inviteLoading={inviteLoading}
                                        handleInviteMember={handleInviteMember}
                                        inviteData={inviteData}
                                        inviteErr={inviteErr}
                                    />
                                )}

                                {isTrainer && (
                                    <InviteLinkCard
                                        activeTeamId={activeTeamId}
                                        handleGenerateInvite={handleGenerateInvite}
                                        iLoading={iLoading}
                                        iData={iData}
                                        iError={iError}
                                    />
                                )}

                            </Stack>
                        </Grid>

                        <Grid lg={8}>
                            {isTrainer && activeTeamId && (
                                <MemberRoster
                                    membersData={membersData}
                                    isTrainer={isTrainer}
                                    profile={profile}
                                    handleUpdateSecondaryRole={handleUpdateSecondaryRole}
                                    handleRemoveMember={handleRemoveMember}
                                />
                            )}
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Fade>
    );
};

export default TeamManagement;
